.page {
  padding: var(--space-6) 0 var(--space-12);
  width: 100%;
  background: var(--bg-base);
  overflow-x: clip;
}

/* ── Hero: the composer owns the fold ─────────────────────── */

.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  /* Centred column, but the page gutter still comes from the layout shell so
     the margins never grow with the viewport. */
  max-width: 720px;
  margin: 0 auto;
  /* Tighter at the top than the bottom: the title is now the first thing on
     the page, so it shouldn't sit in the middle of a void. */
  padding: var(--space-8) 0 var(--space-12);
}

.heroTitle {
  margin: 0;
  font-family: var(--font-sans);
  /* 28px, not 36px — the longer title reads as a heading at this size rather
     than a banner, and sits closer in weight to the section titles below. */
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
  line-height: 1.15;
}

.heroSub {
  margin: var(--space-3) 0 0 0;
  font-size: var(--text-lg);
  color: var(--text-muted);
  line-height: var(--leading-relaxed);
  /* No measure cap here: `.hero` is already capped at 720px, and this sentence
     is 74 characters — at 18px that is ~666px, so a 52ch limit (~515px) broke
     it onto a second line holding the single word "data." Two caps competing
     for the same job, and the tighter one was not the one that mattered. It
     still wraps below ~700px, where the parent is the constraint. */
  max-width: none;
}

/* ── Composer ─────────────────────────────────────────────── */

.composer {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: var(--space-8);
  padding: var(--space-3);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-xl);
  text-align: left;
  transition:
    border-color var(--duration-base) var(--ease-default),
    box-shadow var(--duration-base) var(--ease-default);
}

.composer:focus-within {
  border-color: var(--ai-accent);
  box-shadow: 0 0 0 1px var(--ai-accent), 0 0 24px var(--ai-accent-glow);
}

/* Two lines of breathing room, not three — an oversized empty box reads as a
   form to fill in rather than a prompt to answer. */
.composerInput {
  width: 100%;
  min-height: 48px;
  padding: var(--space-2);
  background: none;
  border: none;
  outline: none;
  resize: none;
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  color: var(--text-primary);
  line-height: var(--leading-relaxed);
}

/* The wrapper owns the focus ring, so the textarea must not draw its own.
   `outline: none` above isn't enough: global.css has a bare `:focus-visible`
   rule painting a yellow outline on everything, and at equal specificity the
   winner comes down to stylesheet order. Matching the state explicitly
   (0,2,0 vs 0,1,0) settles it regardless of injection order.
   Focus stays visible — `.composer:focus-within` draws the accent ring. */
.composerInput:focus-visible {
  outline: none;
}

.composerInput::placeholder {
  color: var(--text-muted);
}

.composerFoot {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-3);
  padding-top: var(--space-2);
}

.examples {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  min-width: 0;
}

/* Example prompts double as the empty-state guidance — click to fill.
   Never truncated: a half-shown example ("Compare Max and Peacock on sub…")
   teaches nothing, and these exist precisely to show the shape of a good ask.
   They wrap to a second row instead. */
.example {
  padding: 5px var(--space-3);
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;
  transition:
    color var(--duration-fast) var(--ease-default),
    border-color var(--duration-fast) var(--ease-default);
}

.example:hover {
  color: var(--text-primary);
  border-color: var(--ai-accent);
}

.send {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: none;
  border-radius: var(--radius-md);
  background: var(--ai-gradient);
  color: var(--text-primary);
  cursor: pointer;
  transition:
    background var(--duration-base) var(--ease-default),
    box-shadow var(--duration-base) var(--ease-default),
    opacity var(--duration-fast) var(--ease-default);
}

.send:hover:not(:disabled) {
  background: var(--ai-gradient-flip);
  box-shadow: 0 2px 14px var(--ai-accent-glow);
}

.send:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ── Tabs ─────────────────────────────────────────────────── */

/* A rule the full width of the content, with the active tab sitting on it —
   the tabs read as belonging to everything below rather than floating above
   the first section. */
.tabs {
  display: flex;
  gap: var(--space-5);
  margin-bottom: var(--space-8);
  border-bottom: 1px solid var(--border-subtle);
}

.tab {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 0 var(--space-3) 0;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--weight-semi);
  color: var(--text-muted);
  cursor: pointer;
  transition:
    color var(--duration-fast) var(--ease-default),
    border-color var(--duration-fast) var(--ease-default);
}

.tab:hover {
  color: var(--text-secondary);
}

.tabActive {
  color: var(--text-primary);
  border-bottom-color: var(--ai-accent);
}

.tab:focus-visible {
  outline: 2px solid var(--ai-accent);
  outline-offset: 3px;
  border-radius: var(--radius-sm);
}

.tabCount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--radius-pill);
  background: var(--bg-elevated);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
}

.tabActive .tabCount {
  background: var(--ai-surface);
  color: var(--ai-accent);
}

/* ── Sections ─────────────────────────────────────────────── */

.section {
  margin-bottom: var(--space-10);
}

.sectionHead {
  margin-bottom: var(--space-5);
}

.sectionTitle {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  line-height: 1.25;
}

.sectionSub {
  margin: var(--space-1) 0 0 0;
  font-size: var(--text-base);
  color: var(--text-muted);
}

/* ── Saved ────────────────────────────────────────────────── */

.savedGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: var(--space-3);
}

.savedCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  text-align: left;
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-default),
    background var(--duration-fast) var(--ease-default);
}

.savedCard:hover {
  background: var(--bg-elevated);
  border-color: var(--ai-accent);
}

.savedName {
  font-size: var(--text-base);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
}

.savedMeta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.savedDate {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.savedDot {
  opacity: 0.5;
}

/* ── Template tiles ───────────────────────────────────────── */

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--space-4);
}

/* Tile anatomy: poster art, the analysis as the headline, the bound subject
   under it, then the description. The analysis leads because that is what the
   reader is choosing — the subject is how it's being demonstrated. */
.tile {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  transition:
    border-color var(--duration-base) var(--ease-default),
    transform var(--duration-base) var(--ease-default),
    box-shadow var(--duration-base) var(--ease-default);
}

.tile:hover {
  border-color: var(--ai-accent);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

/* The card is a container, not the control — the alternate-subject chips are
   separate buttons, so a nested button would be invalid markup. */
.tileMain {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
}

/* Keyboard traversal needs a visible target — the hover lift alone leaves a
   tabbing user with no idea where they are. */
.tileMain:focus-visible,
.tileAlt:focus-visible,
.savedCard:focus-visible,
.emptyBtn:focus-visible,
.example:focus-visible {
  outline: 2px solid var(--ai-accent);
  outline-offset: 2px;
}

/* Poster art carries the tile, so it gets a taller frame than a wireframe
   thumbnail would — 16/9 crops most posters past recognition. */
.tileImage {
  display: block;
  aspect-ratio: 5 / 3;
  border-bottom: 1px solid var(--border-subtle);
  overflow: hidden;
}

.tileBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
}

.tileName {
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  line-height: 1.25;
}

/* The subject this analysis is shown running against. Subordinate to the
   analysis name, but in the accent so it reads as concrete and clickable. */
.tileSubject {
  margin-top: -4px;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--ai-accent);
}

.tileDesc {
  font-size: var(--text-base);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

/* ── Alternate subjects ───────────────────────────────────── */

.tileAlts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: 0 var(--space-4) var(--space-4);
  margin-top: auto;
}

.tileAltsLabel {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.tileAlt {
  padding: 3px var(--space-3);
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    color var(--duration-fast) var(--ease-default),
    border-color var(--duration-fast) var(--ease-default);
}

.tileAlt:hover {
  color: var(--text-primary);
  border-color: var(--ai-accent);
}

/* ── Empty state (My workflows) ───────────────────────────── */

.emptyState {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-8);
  background: var(--bg-card);
  border: 1px dashed var(--border-default);
  border-radius: var(--radius-lg);
}

.emptyTitle {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
}

.emptyText {
  margin: 0;
  font-size: var(--text-base);
  color: var(--text-muted);
  line-height: var(--leading-relaxed);
  max-width: 56ch;
}

.emptyBtn {
  margin-top: var(--space-2);
  padding: var(--space-2) var(--space-4);
  background: var(--ai-gradient);
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-default);
}

.emptyBtn:hover {
  background: var(--ai-gradient-flip);
}

@media (max-width: 640px) {
  .page {
    padding: var(--space-4) 0 var(--space-10);
  }

  .hero {
    padding: var(--space-6) 0 var(--space-8);
  }

  .heroTitle {
    font-size: var(--text-xl);
  }

  .examples {
    display: none;
  }
}
